import { useContext, useEffect, useState } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import {
  CanMakePaymentResult,
  PaymentRequest,
  PaymentRequestPaymentMethodEvent,
} from '@stripe/stripe-js';
import { useIntl } from 'react-intl';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';

import { trackBingAdsConversion } from 'app/lib/bing';
import { useAnalyticsContext } from 'app/shared/utils';
import {
  Event,
  EventAttendee,
  Order,
  PaymentMethod,
  PromoCodeWithCharges,
  User,
} from 'app/shared/typings';
import { AlertContext } from 'app/shared/context';
import {
  createPaymentIntent,
  handlePaymentIntentResponse,
  ShowVirtualPayment,
  usePriceBreakdown,
  useShowVirtualWalletPayment,
  useVisaPartnershipCheckoutLogic,
} from 'app/fan/utils/checkout';
import {
  PAYMENT_METHOD_APPLE_PAY,
  PAYMENT_METHOD_GOOGLE_PAY,
  summaryFeeDescription,
  summaryTicketsDescription,
  ThirdPartyPaymentMethodType,
  trackSuccessfulOrder,
  updateCheckoutCookies,
} from 'app/fan/utils/checkout';

import { completeThePayment } from './completeThePayment';

export interface StripeVirtualWallet {
  paymentRequest?: PaymentRequest;
  canMakeVirtualPaymentResult?: CanMakePaymentResult;
  showVirtualPayment: ShowVirtualPayment;
  paymentStatus: VirtualWalletPaymentStatus;
  order?: Order;
}

export enum VirtualWalletPaymentStatus {
  NONE = 'NONE',
  LOADING = 'LOADING',
  COMPLETE = 'COMPLETE',
}

export interface VirtualWalletParams {
  user: User | null;
  event?: Event | null;
  eventAttendee?: EventAttendee | null;
  numberOfTickets: number;
  isGuestCheckout?: boolean;
  isExpressCheckout?: boolean;
  optinGuestMarketing: boolean;
  promoCodeWithCharges?: PromoCodeWithCharges;
  savedCards?: PaymentMethod[];
}

export const useVirtualWallet = (
  virtualWalletParams: VirtualWalletParams
): StripeVirtualWallet | undefined => {
  const intl = useIntl();
  const stripe = useStripe();
  const elements = useElements();

  const [paymentStatus, setPaymentStatus] =
    useState<VirtualWalletPaymentStatus>(VirtualWalletPaymentStatus.NONE);

  const navigate = useNavigate();
  const {
    user: loggedInUser,
    event,
    eventAttendee,
    numberOfTickets,
    optinGuestMarketing,
    isExpressCheckout,
    isGuestCheckout,
    promoCodeWithCharges,
    savedCards,
  } = virtualWalletParams;

  const [stripeVirtualWallet, setStripeVirtualWallet] =
    useState<StripeVirtualWallet>();

  const visaPartnershipCheckoutLogic = useVisaPartnershipCheckoutLogic({
    event: event || null,
    eventId: event?.id,
    eventAttendee: eventAttendee || null,
    numberOfTickets,
    isExpressCheckout: isExpressCheckout || false,
    savedCards,
  });

  const showVirtualPayment = useShowVirtualWalletPayment({
    visaPartnershipCheckoutLogic,
    stripeVirtualWallet,
    event,
    isExpressCheckout,
  });

  const alertContext = useContext(AlertContext);
  const { formatMessage } = useIntl();

  const {
    finalPrice,
    fee,
    feeType,
    numberOfTicketsWithFee,
    promoCodeDiscountAmount,
    eventPrice,
    currencySymbol,
  } = usePriceBreakdown({
    numberOfTickets,
    promoCodeWithCharges,
    numberOfVisaFreeTickets:
      visaPartnershipCheckoutLogic.numberOfVisaFreeTickets,
    canRedeemAndIsRedeemingVisaFreeTicket:
      visaPartnershipCheckoutLogic.canRedeemAndIsRedeemingVisaFreeTicket,
    event: event || null,
    eventAttendee: eventAttendee || null,
  });
  const [
    virtualWalletInitializationError,
    setVirtualWalletInitializationError,
  ] = useState(false);

  const [paymentMethodType, setPaymentMethodType] = useState<
    ThirdPartyPaymentMethodType | undefined
  >(undefined);
  const { trackAnalyticsEvent } = useAnalyticsContext();
  const eventAttendeeId = eventAttendee ? eventAttendee.id : null;

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const { paymentRequest, canMakeVirtualPaymentResult } = stripeVirtualWallet
    ? stripeVirtualWallet
    : { paymentRequest: undefined, canMakeVirtualPaymentResult: undefined };
  const { country, cachedSlug } = event
    ? event.city
    : { country: null, cachedSlug: null };

  const amount = Math.round(finalPrice * 100);
  const feesTotalAmount = Math.round(fee * numberOfTicketsWithFee * 100);
  const ticketsTotalAmount = eventPrice * numberOfTickets * 100;

  const virtualPaymentsValid = !!(
    stripe &&
    elements &&
    event &&
    !event.onPresale &&
    amount > 0 &&
    country &&
    !virtualWalletInitializationError
  );

  const hasFees = fee > 0 && numberOfTicketsWithFee > 0;
  const feeLabel = summaryFeeDescription({
    currencySymbol,
    fee,
    feeType,
    numberOfTicketsWithFee,
    intl,
  });
  const isRedeemingPromoCode =
    promoCodeWithCharges && promoCodeWithCharges.isValid;
  const ticketsLabel = summaryTicketsDescription({
    currencySymbol,
    eventPrice,
    numberOfTickets,
    intl,
  });

  const errorLoggingData = {
    event,
    stripe,
    paymentMethodType,
    canMakeVirtualPaymentResult:
      stripeVirtualWallet?.canMakeVirtualPaymentResult,
    numberOfTickets,
    canRedeemAndIsRedeemingVisaFreeTicket:
      visaPartnershipCheckoutLogic.canRedeemAndIsRedeemingVisaFreeTicket,
    finalPrice,
  };

  useEffect(() => {
    if (virtualPaymentsValid && !paymentRequest && country && stripe) {
      try {
        const ticketsDetails = [
          { label: ticketsLabel, amount: ticketsTotalAmount },
        ];
        const promoCodeDetails = isRedeemingPromoCode
          ? [
              {
                label: 'Discount Applied',
                amount: -promoCodeDiscountAmount * 100,
              },
            ]
          : [];
        const feeDetails = hasFees
          ? [{ label: feeLabel, amount: feesTotalAmount }]
          : [];

        const itemsDetails = ticketsDetails
          .concat(feeDetails)
          .concat(promoCodeDetails);
        // Create new paymentRequest
        const newPaymentRequest = stripe.paymentRequest({
          country: country.countryCode,
          currency: event?.city?.currencyCode || '',
          total: {
            label: 'Total',
            amount,
          },
          displayItems: itemsDetails,
          requestPayerName: true,
          requestPayerEmail: true,
        });

        newPaymentRequest.canMakePayment().then((result) => {
          if (result) {
            if (result.applePay) {
              setPaymentMethodType(PAYMENT_METHOD_APPLE_PAY);
            } else if (result.googlePay) {
              setPaymentMethodType(PAYMENT_METHOD_GOOGLE_PAY);
            }
          }

          setStripeVirtualWallet({
            paymentRequest: newPaymentRequest,
            canMakeVirtualPaymentResult: result
              ? result
              : { applePay: false, googlePay: false },
            showVirtualPayment,
            paymentStatus: VirtualWalletPaymentStatus.NONE,
          });
        });
      } catch (e) {
        console.error(
          'Virtual Wallet Error - Stripe error creating a paymentRequest instance',
          errorLoggingData,
          JSON.stringify(e)
        );
        setVirtualWalletInitializationError(true);
      }
    }
  }, [
    virtualPaymentsValid,
    paymentRequest,
    country,
    stripe,
    amount,
    setStripeVirtualWallet,
    canMakeVirtualPaymentResult,
    feesTotalAmount,
    ticketsLabel,
    ticketsTotalAmount,
    isRedeemingPromoCode,
    promoCodeDiscountAmount,
    hasFees,
    feeLabel,
  ]);

  useEffect(() => {
    if (
      virtualPaymentsValid &&
      canMakeVirtualPaymentResult &&
      paymentRequest &&
      (canMakeVirtualPaymentResult.applePay ||
        canMakeVirtualPaymentResult.googlePay)
    ) {
      const ticketsDetails = [
        { label: ticketsLabel, amount: ticketsTotalAmount },
      ];
      const promoCodeDetails = isRedeemingPromoCode
        ? [
            {
              label: 'Discount Applied',
              amount: Math.round(-promoCodeDiscountAmount * 100),
            },
          ]
        : [];
      const feeDetails = hasFees
        ? [{ label: feeLabel, amount: feesTotalAmount }]
        : [];

      const itemsDetails = ticketsDetails
        .concat(feeDetails)
        .concat(promoCodeDetails);
      // Update existing paymentRequest
      paymentRequest.update({
        currency: event?.city?.currencyCode || 'usd',
        total: {
          label: 'Total',
          amount,
        },
        displayItems: itemsDetails,
      });
    }
  }, [
    amount,
    country,
    virtualPaymentsValid,
    canMakeVirtualPaymentResult,
    paymentRequest,
    ticketsLabel,
    ticketsTotalAmount,
    isRedeemingPromoCode,
    promoCodeDiscountAmount,
    hasFees,
    feeLabel,
    feesTotalAmount,
  ]);

  const sharedAnalyticsArgs = {
    checkout_id: eventAttendeeId,
    payment_method_type: paymentMethodType,
    is_visa_validated: visaPartnershipCheckoutLogic.visaPreSaleEligible,
    city_slug: event?.city?.cachedSlug,
    event_id: event?.id,
    is_guest_checkout: !!isGuestCheckout,
    user_id: loggedInUser?.id,
    rebrand: true,
  };

  useEffect(() => {
    async function handleCompletePayment(
      paymentMethodEvent: PaymentRequestPaymentMethodEvent
    ) {
      if (!loggedInUser && !isGuestCheckout) {
        const errorMessage =
          'Attempted non-guest checkout without a logged-in user';
        console.error(
          `Virtual Wallet Error - ${errorMessage}`,
          errorLoggingData
        );

        trackAnalyticsEvent('Virtual Wallet Payment failed', {
          ...sharedAnalyticsArgs,
          checkout_version: 2,
          error_message: errorMessage,
          is_express_checkout: isExpressCheckout,
        });

        isExpressCheckout &&
          trackAnalyticsEvent('Express Checkout Failed', {
            ...sharedAnalyticsArgs,
            reason: 'Authentication error',
            details: errorMessage,
          });

        throw new Error('You are not logged in');
      }

      const paymentMethodId = paymentMethodEvent.paymentMethod.id;
      const genericError = formatMessage({
        id: 'checkout.genericError',
      });

      if (!paymentMethodId || !event || !stripe) {
        const missingVariable = !paymentMethodId
          ? 'paymentMethodId'
          : !event
            ? 'event'
            : 'stripe';

        trackAnalyticsEvent('Virtual Wallet Payment failed', {
          ...sharedAnalyticsArgs,
          checkout_version: 2,
          is_express_checkout: isExpressCheckout,
          error_message: `'${missingVariable}' is not defined`,
        });

        if (isExpressCheckout) {
          trackAnalyticsEvent('Express Checkout Failed', {
            ...sharedAnalyticsArgs,
            reason: 'Missing required variable',
            details: `'${missingVariable}' is not defined`,
          });
        }
        console.error(
          'Virtual Wallet Error - Stripe error completing the payment',
          errorLoggingData
        );
        throw new Error(genericError);
      }

      try {
        setPaymentStatus(VirtualWalletPaymentStatus.LOADING);
        const { billingRegion, id: eventId } = event;
        const numTickets = numberOfTickets;
        const userEmail = paymentMethodEvent.payerEmail;
        const fullNameParts = paymentMethodEvent.payerName?.split(' ');
        const firstName = fullNameParts && fullNameParts[0];
        const lastName =
          fullNameParts && fullNameParts[fullNameParts.length - 1];

        const paymentIntentResponse = await createPaymentIntent({
          paymentMethodId,
          amount: Math.round(finalPrice * 100),
          currency: event.city?.currencyCode || '',
          billingRegion,
          eventId,
          numTickets,
          storePaymentMethod: false,
          isVirtualWallet: true,
          isGuestCheckout: !!isGuestCheckout,
          email: userEmail,
          firstName,
          lastName,
          optinGuestMarketing,
        });

        if (paymentIntentResponse?.data) {
          await completeThePayment({
            stripe,
            paymentMethodEvent,
            paymentIntentResponse: paymentIntentResponse.data,
            trackAnalyticsEvent,
            sharedAnalyticsArgs,
            isExpressCheckout,
          });

          const paymentIntentUser =
            paymentIntentResponse.data?.createPaymentIntent?.user;

          const { data, errors } = await handlePaymentIntentResponse({
            paymentIntentResponse,
            stripe,
            billingRegion,
            eventId,
            numTickets,
            promoCode: promoCodeWithCharges,
            checkoutVersion: 2,
            isExpressCheckout,
            guestUserId: isGuestCheckout ? paymentIntentUser.id : undefined,
          });

          const orderId = data && data.createOrder && data.createOrder.id;
          if (orderId) {
            trackSuccessfulOrder({
              orderId,
              event,
              numberOfTickets,
              finalPrice,
              promoCodeWithCharges,
              fee,
            });

            setStripeVirtualWallet({
              ...stripeVirtualWallet,
              order: data.createOrder,
              canMakeVirtualPaymentResult,
              showVirtualPayment,
              paymentStatus,
              paymentRequest,
            });

            const orderEventAttendee = data.createOrder?.eventAttendee;
            const orderEventAttendeeUser = orderEventAttendee?.user;
            const attendeeTotalTickets = orderEventAttendee?.ticketsCount;

            updateCheckoutCookies({
              event,
              loggedInUser,
              orderEventAttendeeUser,
              attendeeTotalTickets,
              numberOfTickets,
            });

            trackAnalyticsEvent('Virtual Wallet Payment Completed', {
              ...sharedAnalyticsArgs,
              payment_method: paymentMethodType, // New property is payment_method_type, but leave this for historical data reasons
              checkout_version: 2,
              is_guest_user: paymentIntentUser.isGuest,
              is_express_checkout: isExpressCheckout,
            });

            if (isExpressCheckout) {
              trackAnalyticsEvent('Express Checkout Completed', {
                ...sharedAnalyticsArgs,
                is_guest_user: paymentIntentUser.isGuest,
              });

              const currency = event.city?.currencyCode?.toUpperCase();

              // In Regular Checkout (non-express), this gets tracked during the OrderCompleteStage
              trackAnalyticsEvent('Checkout Complete', {
                ...sharedAnalyticsArgs,
                is_guest_user: paymentIntentUser.isGuest,
                is_express_checkout: true,
                category: 'DiscoveryEvent',
                value: finalPrice,
                revenue: finalPrice,
                order_id: orderId,
                checkout_id: data.createOrder?.eventAttendee?.id,
                currency,
                email: (userEmail || paymentIntentUser.email)
                  ?.toLowerCase()
                  .trim(),
                country: event?.city?.country.countryCode,
              });

              // In Regular Checkout (non-express), this gets called during the OrderCompleteStage
              trackBingAdsConversion({
                eventId: event.id,
                finalPrice,
                currency: currency || '',
                pageType: 'PRODUCT', // Since this is Express Checkout, we are on a product page, not a purchase page
              });
            }

            setTimeout(() => {
              if (isExpressCheckout) {
                if (isGuestCheckout) {
                  const params = {
                    eaid: data?.createOrder?.eventAttendee?.id || '',
                    uid: paymentIntentUser?.id.toString() || '',
                    ticket_quantity: numberOfTickets.toString(),
                  };

                  navigate({
                    pathname: `/checkout/${eventId}`,
                    search: `?${createSearchParams(params)}`,
                  });
                }
                window.location.reload();
              } else {
                setPaymentStatus(VirtualWalletPaymentStatus.COMPLETE);
              }
            }, 1900);
          } else {
            const error = errors?.[0];
            const errorMessage =
              error?.message ||
              'The server returned a 200 status code, but did not return an order id';
            console.error(
              `Virtual Wallet Error - ${errorMessage}`,
              errorLoggingData
            );
            trackAnalyticsEvent('Virtual Wallet Payment failed', {
              ...sharedAnalyticsArgs,
              checkout_version: 2,
              is_guest_user: paymentIntentUser.isGuest,
              is_express_checkout: isExpressCheckout,
              error_message: errorMessage,
            });

            isExpressCheckout &&
              trackAnalyticsEvent('Express Checkout Failed', {
                ...sharedAnalyticsArgs,
                reason: error ? 'Server Error' : 'Missing final order data',
                details: error || errorMessage,
                is_guest_user: paymentIntentUser.isGuest,
              });

            return alertContext.addAlert({
              message: genericError,
              type: 'error',
            });
          }
        }
      } catch (e) {
        setPaymentStatus(VirtualWalletPaymentStatus.NONE);
        paymentMethodEvent.complete('fail');
        const errorMessage = (e as any).message || genericError;

        trackAnalyticsEvent('Virtual Wallet Payment failed', {
          ...sharedAnalyticsArgs,
          payment_method: paymentMethodType, // New property is payment_method_type, but leave this for historical data reasons
          checkout_version: 2,
          is_express_checkout: isExpressCheckout,
          error_message: errorMessage,
        });

        isExpressCheckout &&
          trackAnalyticsEvent('Express Checkout Failed', {
            ...sharedAnalyticsArgs,
            reason: 'Server Error',
            details: e,
          });
        console.error(
          `Virtual Wallet Error - ${errorMessage}`,
          errorLoggingData
        );

        alertContext.addAlert({
          message: errorMessage,
          type: 'error',
        });
      }
    }

    if (paymentRequest && stripe && elements && event) {
      // @ts-ignore
      if (!paymentRequest.hasRegisteredListener('paymentmethod')) {
        paymentRequest.on('paymentmethod', handleCompletePayment);
      }

      return () => {
        paymentRequest.off('paymentmethod');
      };
    }
    return;
  }, [
    paymentRequest,
    stripe,
    event,
    eventAttendeeId,
    fee,
    finalPrice,
    formatMessage,
    visaPartnershipCheckoutLogic.visaPreSaleEligible,
    numberOfTickets,
    promoCodeWithCharges,
    token,
    trackAnalyticsEvent,
    loggedInUser,
    elements,
    isGuestCheckout,
    isExpressCheckout,
    paymentMethodType,
    sharedAnalyticsArgs,
  ]);

  useEffect(() => {
    if (paymentRequest && event) {
      paymentRequest.on('cancel', () => {
        trackAnalyticsEvent('Virtual Wallet Payment canceled', {
          ...sharedAnalyticsArgs,
          payment_method: paymentMethodType, // New property is payment_method_type, but leave this for historical data reasons
          checkout_version: 2,
          is_express_checkout: isExpressCheckout,
        });

        isExpressCheckout &&
          trackAnalyticsEvent('Express Checkout Failed', {
            ...sharedAnalyticsArgs,
            reason: 'Payment dialog canceled',
          });
      });

      return () => {
        paymentRequest.off('cancel');
      };
    }
    return;
  }, [
    eventAttendeeId,
    cachedSlug,
    event,
    visaPartnershipCheckoutLogic.visaPreSaleEligible,
    paymentRequest,
    trackAnalyticsEvent,
    paymentMethodType,
    sharedAnalyticsArgs,
    isExpressCheckout,
    loggedInUser,
    isGuestCheckout,
  ]);

  useEffect(() => {
    if (!stripeVirtualWallet) {
      return;
    }

    const currentShowVirtualPayment = JSON.stringify(
      stripeVirtualWallet.showVirtualPayment
    );
    const newShowVirtualPayment = JSON.stringify(showVirtualPayment);

    if (currentShowVirtualPayment !== newShowVirtualPayment) {
      setStripeVirtualWallet({
        ...stripeVirtualWallet,
        showVirtualPayment,
      });
    }
  }, [showVirtualPayment]);

  useEffect(() => {
    if (!stripeVirtualWallet) {
      return;
    }

    const currentPaymentStatus = stripeVirtualWallet.paymentStatus;
    const newPaymentStatus = paymentStatus;

    if (currentPaymentStatus !== newPaymentStatus) {
      setStripeVirtualWallet({
        ...stripeVirtualWallet,
        paymentStatus,
      });
    }
  }, [paymentStatus]);

  return stripeVirtualWallet;
};
